import { useEffect, useState } from 'react';
import { graphql } from 'react-apollo';

import type { ApolloError } from '@apollo/client';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';

import type Item from 'bundles/author-course/models/Item';
import useIsMounted from 'bundles/common/hooks/useIsMounted';
import type {
  GroupItemGradesV1,
  GroupItemGradesV1ByGroupAndItemQuery,
  GroupItemGradesV1ByGroupAndItemQueryVariables,
  GroupItemGradesV1Connection,
} from 'bundles/naptimejs/resources/__generated__/GroupItemGradesV1';

type InputProps = {
  groupId: string;
  item: Item;
  itemId?: string;
};

export type WithGroupItemGradesProps = InputProps & {
  groupItemGrades?: GroupItemGradesV1[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refetchGroupItemGrades?: (variables?: GroupItemGradesV1ByGroupAndItemQueryVariables) => Promise<any>;
};

/**
 * Page size limit. We want to fetch all items until we implement a pagination for it.
 * TODO: Make pagination and fetch it in smaller pages.
 */
const LIMIT = 10000;

/* eslint-disable graphql/template-strings */
export const groupItemGradesQuery = gql`
  query GroupItemGradesV1ByGroupAndItemQuery($groupId: String!, $itemId: String!, $limit: String!, $start: Int) {
    GroupItemGradesV1 @naptime {
      byGroupAndItem(groupId: $groupId, itemId: $itemId, limit: $limit, start: $start) {
        elements {
          id
          details
        }
        paging {
          next
          total
        }
      }
    }
  }
`;
/* eslint-enable graphql/template-strings */

/**
 * @deprecated
 * Migrate from `withGroupItemGrades` HOC to `useGroupItemGrades` hook
 */
const withGroupItemGrades = graphql<
  InputProps,
  GroupItemGradesV1ByGroupAndItemQuery,
  GroupItemGradesV1ByGroupAndItemQueryVariables,
  WithGroupItemGradesProps
>(groupItemGradesQuery, {
  options: ({ groupId, item, itemId }) => ({
    variables: { groupId, itemId: itemId || item.id, limit: LIMIT },
  }),
  skip: ({ groupId, item, itemId }) => {
    return !groupId || (!item && !itemId);
  },
  props: ({ data, ownProps }) => {
    if (data?.GroupItemGradesV1?.byGroupAndItem?.elements) {
      const groupItemGrades = data?.GroupItemGradesV1?.byGroupAndItem?.elements;
      const refetchGroupItemGrades = data.refetch;
      return {
        ...ownProps,
        groupItemGrades,
        refetchGroupItemGrades,
      };
    }
    return ownProps;
  },
});

export const DEFAULT_BATCH_LIMIT = 300;

const isStringifiedNumber = (str: string) => /^[0-9]*$/.test(str);

/**
 * Generates an array of offsets, skipping the 0-th page (it was fetched with
 * initial useQuery).
 */
export const getOffsets = (totalRecords: number, limit: number) => {
  const totalPages = Math.ceil(totalRecords / limit);
  return Array.from(Array(totalPages - 1), (_, i) => (i + 1) * limit);
};

/**
 * Merge result of "fetchMore" with previously loaded data
 */
const updateQueryAfterFetchMore = (
  prevResult: GroupItemGradesV1ByGroupAndItemQuery,
  fetchMoreResult: GroupItemGradesV1ByGroupAndItemQuery,
  setLastToNull: boolean
) => {
  return {
    ...prevResult,
    GroupItemGradesV1: {
      ...prevResult.GroupItemGradesV1,
      byGroupAndItem: {
        ...prevResult.GroupItemGradesV1?.byGroupAndItem,
        elements: [
          ...(prevResult.GroupItemGradesV1?.byGroupAndItem?.elements || []),
          ...(fetchMoreResult.GroupItemGradesV1?.byGroupAndItem?.elements || []),
        ],
        paging: {
          ...(fetchMoreResult.GroupItemGradesV1?.byGroupAndItem as Paginated)?.paging,
          next: setLastToNull ? '' : (fetchMoreResult.GroupItemGradesV1?.byGroupAndItem as Paginated)?.paging.next,
        },
      },
    },
  } as GroupItemGradesV1ByGroupAndItemQuery;
};

export type UseGroupItemGradesResult = {
  loading: boolean;
  groupItemGrades?: GroupItemGradesV1[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  refetchGroupItemGrades: (variables?: GroupItemGradesV1ByGroupAndItemQueryVariables) => Promise<any>;
  error?: ApolloError;
};

export type Paging = {
  next?: string | null;
  total?: number | null;
};
export type Paginated = GroupItemGradesV1Connection & { paging: Paging };

/**
 * Fetches GroupItemGradesV1 data in a batched manner:
 * 1. initial query to get `batchLimit` number of records;
 * 2. and then `((total records / batchLimit) - 1)` requests to get the rest.
 * These are sent in parallel or sequential manner depending on `paging`
 * structure returned by the first request.
 *
 * Note: do not use for Team SGA until this issue is resolved:
 * https://coursera.slack.com/archives/C02LNK0FV8C/p1706636432737369
 * in #lce-grading-private
 *
 * Returns:
 *   - `loading` indicating requests are in progress.
 *   - `groupItemGrades` as soon as the first batch is completed. The object is
 *      updated with every `fetchMore` result. In case any of the batched
 *      requests fail, `groupItemGrades` keeps results from all successful
 *      requests (we can change this behavior if desired).
 *   - `error` contains Apollo Error returned from either initial request, or
 *      any of the subsequent `fetchMore` requests.
 */
export const useGroupItemGrades = ({
  groupId,
  item,
  itemId,
  batchLimit = DEFAULT_BATCH_LIMIT,
}: InputProps & { batchLimit?: number }): UseGroupItemGradesResult => {
  const [err, setErr] = useState<ApolloError>();
  const [fetchAllStatus, setFetchAllStatus] = useState<'none' | 'fetching' | 'done'>('none');
  const isMounted = useIsMounted();

  // Intentionally `start=undefined` for the first request, as we don't know
  // whether this item expects a stringified "offset" or a "cursor" pattern.
  const { data, refetch, fetchMore } = useQuery<
    GroupItemGradesV1ByGroupAndItemQuery,
    GroupItemGradesV1ByGroupAndItemQueryVariables & { limit: number; start?: number }
  >(groupItemGradesQuery, {
    variables: { groupId, itemId: itemId || item.id, limit: batchLimit, start: undefined },
    skip: !groupId || (!item && !itemId),
    notifyOnNetworkStatusChange: true,
    onError: (e) => {
      setErr(e);
      setFetchAllStatus('done');
    },
  });

  const { next, total } = (data?.GroupItemGradesV1?.byGroupAndItem as Paginated)?.paging || {};

  useEffect(() => {
    // This way we track initial "fetch all records" process to show the
    // `loading` state. We don't change `fetchAllStatus` back to `none` or
    // `fetching` during "refetch".
    if (data && next && fetchAllStatus === 'none' && !err) {
      setFetchAllStatus('fetching');
    }

    if (data && !next) {
      setFetchAllStatus('done');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, next, fetchAllStatus, err]);

  useEffect(() => {
    // For TMR and LTI the endpoint returns `next` as some guid, assuming
    // "cursor" pagination pattern. Only sequential requests are possible.
    async function fetchNext() {
      try {
        await fetchMore({
          variables: { start: next },
          updateQuery: (prev, { fetchMoreResult }) => updateQueryAfterFetchMore(prev, fetchMoreResult, false),
        });
      } catch (e) {
        if (isMounted()) {
          setErr(e);
          setFetchAllStatus('done');
        }
      }
    }

    // For Individual SGA the endpoint returns `paging={next, total}` (`next` is
    // a stringified number) assuming "offset" pagination pattern. In this case
    // we can pre-calculate offsets for all pages and send parallel requests.
    // Same for Team SGA, however the endpoint's `paging` has known issues for
    // this item type.
    async function fetchAll(totalRecords: number, limit: number) {
      try {
        // Create a `fetchMore` promise for each offset.
        const promises = getOffsets(totalRecords, limit).map((offset) =>
          fetchMore({
            variables: { start: `${offset}` },
            updateQuery: (prev, { fetchMoreResult }) => updateQueryAfterFetchMore(prev, fetchMoreResult, true),
          })
        );
        await Promise.all(promises);
      } catch (e) {
        if (isMounted()) {
          setErr(e);
        }
      }
      if (isMounted()) {
        setFetchAllStatus('done');
      }
    }

    if (next) {
      if (isStringifiedNumber(next || '') && total) {
        fetchAll(total, batchLimit);
      } else {
        fetchNext();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [next, total]);

  return {
    loading: fetchAllStatus !== 'done',
    // We expose partially fetched `data` while `fetchAllStatus=fetching`.
    // Engineers may control rendering by checking `loading` status when needed.
    groupItemGrades: data?.GroupItemGradesV1?.byGroupAndItem?.elements || undefined,
    refetchGroupItemGrades: refetch,
    error: err,
  };
};

export default withGroupItemGrades;
